/**
 * Parse CSV file and map to candidate schema.
 *
 * Canonical columns: User Name, LinkedIn URL, Company, Position, Location, Email, Phone.
 * Recruiters commonly use different names, so each canonical field has a list of
 * accepted synonyms. Matching is case-insensitive and ignores spaces / punctuation:
 * `E-Mail`, `e mail`, `Email Address`, `EMail` all collapse to "email".
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import i18n from '../../i18n';

// Default ISO-2 country for parsing phone numbers without an explicit country
// code. National-format inputs ("0612345678", "06-1234 5678") will be
// interpreted relative to this region. Numbers with an explicit "+" prefix or
// "00" international prefix ignore this default.
const DEFAULT_PHONE_COUNTRY = 'NL';

const REQUIRED_FIELDS = ['first_name', 'linkedin_url', 'email'];

/** Canonical English labels used for synonym / hint deduplication (not for display). */
const FIELD_LABELS_EN = {
  first_name: 'First Name',
  last_name: 'Last Name',
  linkedin_url: 'LinkedIn URL',
  current_company: 'Company',
  current_position: 'Position',
  location: 'Location',
  email: 'Email',
  phone: 'Phone',
};

export const getFieldLabel = (field) => i18n.t(`import.fields.${field}`);

/** Localized field labels (recomputed from current locale). */
export const getFieldLabels = () => ({
  first_name: getFieldLabel('first_name'),
  last_name: getFieldLabel('last_name'),
  linkedin_url: getFieldLabel('linkedin_url'),
  current_company: getFieldLabel('current_company'),
  current_position: getFieldLabel('current_position'),
  location: getFieldLabel('location'),
  email: getFieldLabel('email'),
  phone: getFieldLabel('phone'),
});

/** @deprecated Prefer getFieldLabel / getFieldLabels — kept for callers that read English labels. */
export const FIELD_LABELS = FIELD_LABELS_EN;

export { REQUIRED_FIELDS };

export const OPTIONAL_IMPORT_FIELDS = [
  'last_name',
  'current_company',
  'current_position',
  'location',
  'phone',
];

/** Human-readable header aliases shown in import UI (subset of COLUMN_SYNONYMS). */
export const COLUMN_HEADER_HINTS = {
  first_name: [
    'First', 'Given Name', 'Voornaam', 'Name', 'Full Name', 'Candidate Name', 'Naam',
  ],
  last_name: ['Last', 'Surname', 'Achternaam', 'Family Name'],
  linkedin_url: ['LinkedIn', 'Profile URL', 'LinkedIn Profile', 'LI', 'Social Profile'],
  current_company: ['Employer', 'Organization', 'Bedrijf', 'Werkgever'],
  current_position: ['Job Title', 'Role', 'Current Position', 'Current job', 'Functie', 'Functietitel', 'Beroep', 'Huidige baan'],
  location: ['City', 'Address', 'Plaats', 'Woonplaats', 'Stad', 'Land'],
  email: ['E-Mail', 'Email Address', 'Mail', 'Contact Email'],
  phone: ['Mobile', 'Tel', 'Telefoon', 'Mobiel', 'GSM'],
};

const normalizeHintLabel = (value) =>
  String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Aliases for UI, excluding repeats of the canonical column name. */
export const getColumnHeaderHintsForDisplay = (field) => {
  const canonical = normalizeHintLabel(FIELD_LABELS_EN[field]);
  const localized = normalizeHintLabel(getFieldLabel(field));
  const seen = new Set([canonical, localized].filter(Boolean));

  return (COLUMN_HEADER_HINTS[field] ?? []).filter((hint) => {
    const normalized = normalizeHintLabel(hint);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

// Synonyms must be pre-normalized (lowercase, alphanumeric only) — see normalizeHeader().
// Includes Dutch (NL) headers common in Zoho Recruit / ATS exports.
const COLUMN_SYNONYMS = {
  first_name: [
    'firstname', 'first', 'givenname', 'given', 'fname', 'forename', 'firstnames',
    'voornaam',
  ],
  last_name: [
    'lastname', 'last', 'surname', 'familyname', 'family', 'lname', 'lastnames',
    'achternaam',
  ],
  linkedin_url: [
    'linkedinurl', 'linkedin', 'linkedinprofile', 'linkedinprofileurl',
    'linkedinlink', 'profileurl', 'profilelink', 'liurl', 'liprofile', 'li',
    'sociallink', 'socialprofile', 'social',
  ],
  current_company: [
    'company', 'currentcompany', 'employer', 'currentemployer',
    'organization', 'organisation', 'org', 'workplace', 'currentworkplace',
    'companyname', 'huidigewerkgever', 'werkgever', 'bedrijf',
  ],
  current_position: [
    'position', 'currentposition', 'title', 'jobtitle', 'role', 'currentrole',
    'currenttitle', 'job', 'currentjob', 'designation', 'occupation', 'positiontitle',
    'functie', 'huidigefunctietitel', 'functietitel', 'beroep', 'huidigebaan',
  ],
  location: [
    'location', 'city', 'address', 'country', 'place', 'basedin',
    'currentlocation', 'region', 'where',
    'plaats', 'woonplaats', 'stad', 'adres', 'land',
  ],
  email: [
    'email', 'emailaddress', 'mail', 'contactemail', 'workemail', 'personalemail',
    'emails',
  ],
  phone: [
    'phone', 'phonenumber', 'mobile', 'mobilenumber', 'tel', 'telephone',
    'cell', 'cellphone', 'contactnumber', 'contactphone',
    'telefoon', 'mobiel', 'gsm', 'telnr',
  ],
};

// Fallback for legacy CSVs that have a single combined "name" column instead of
// First Name / Last Name. Only fires when neither first_name nor last_name was
// matched (see buildHeaderMapping). Whatever value is in the column lands in
// first_name as-is and is later persisted into full_name.
const FULL_NAME_FALLBACK_SYNONYMS = [
  'fullname', 'name', 'username', 'candidatename', 'candidate',
  'contactname', 'personname', 'person', 'displayname',
  'volledigenaam', 'naam',
];

const normalizeHeader = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const headerHasData = (row, rawHeader) =>
  Boolean(String(row?.[String(rawHeader || '').toLowerCase()] ?? '').trim());

/**
 * Build a `canonical → original CSV header` mapping (case preserved for display).
 * When several headers match one field (e.g. empty "Huidige functietitel" vs "Functie"),
 * prefer a column that has data in the sample row.
 */
const buildHeaderMapping = (rawHeaders, sampleRow = null) => {
  const mapping = {};
  const used = new Set();
  const normalizedByHeader = new Map(
    rawHeaders.map((header) => [header, normalizeHeader(header)]),
  );
  const synonymSetByCanonical = Object.fromEntries(
    Object.entries(COLUMN_SYNONYMS).map(([canonical, synonyms]) => [
      canonical,
      new Set(synonyms),
    ]),
  );

  for (const [canonical] of Object.entries(COLUMN_SYNONYMS)) {
    const synonyms = synonymSetByCanonical[canonical];
    const matches = rawHeaders.filter(
      (header) => !used.has(header) && synonyms.has(normalizedByHeader.get(header)),
    );
    if (matches.length === 0) continue;

    let chosen = matches[0];
    if (matches.length > 1 && sampleRow) {
      const withData = matches.filter((header) => headerHasData(sampleRow, header));
      if (withData.length > 0) {
        chosen = withData[withData.length - 1];
      }
    }

    mapping[canonical] = chosen;
    used.add(chosen);
  }

  // Fallback: if the CSV has no explicit First / Last columns but does have a
  // combined "Full Name"-style column, route it into first_name. We skip this
  // when last_name is matched on its own to avoid producing "John Smith Smith"
  // from rows like {Full Name: "John Smith", Last Name: "Smith"}.
  if (!mapping.first_name && !mapping.last_name) {
    for (const header of rawHeaders) {
      if (used.has(header)) continue;
      if (FULL_NAME_FALLBACK_SYNONYMS.includes(normalizedByHeader.get(header))) {
        mapping.first_name = header;
        used.add(header);
        break;
      }
    }
  }

  return mapping;
};

const LINKEDIN_HOST_REGEX = /(^|\.)linkedin\.com$/i;
const LINKEDIN_PATH_REGEX = /\/in\/([^\/?#]+)/i;
const LINKEDIN_SLUG_REGEX = /^[\w\-.%]+$/;

// Pragmatic email check: non-empty local, single @, host with a dot, 2+ char TLD.
const EMAIL_REGEX = /^[A-Za-z0-9._%+\-']+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.replace(/^"|"$/g, '').trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.replace(/^"|"$/g, '').trim());
  return result;
};

const buildColumnIndexes = (rawHeaders, headerMapping) => {
  const headerKeys = rawHeaders.map((h) => h.toLowerCase());
  const indexes = {};
  for (const [canonical, rawHeader] of Object.entries(headerMapping)) {
    const idx = headerKeys.indexOf(String(rawHeader).toLowerCase());
    if (idx >= 0) indexes[canonical] = idx;
  }
  return indexes;
};

const rowFromValues = (values, columnIndexes) => {
  const row = {};
  for (const [canonical, idx] of Object.entries(columnIndexes)) {
    const value = values[idx];
    if (value) row[canonical] = String(value).trim();
  }
  return row;
};

/** Parse CSV; only mapped columns are kept (fast for wide ATS exports with 50+ columns). */
const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    return { rawHeaders: [], rows: [], headerMapping: {}, columnIndexes: {} };
  }

  const rawHeaders = parseCSVLine(lines[0]);
  const headerKeys = rawHeaders.map((h) => h.toLowerCase());

  const firstValues = parseCSVLine(lines[1]);
  const sampleRow = {};
  headerKeys.forEach((h, idx) => {
    sampleRow[h] = firstValues[idx] || '';
  });

  const headerMapping = buildHeaderMapping(rawHeaders, sampleRow);
  const columnIndexes = buildColumnIndexes(rawHeaders, headerMapping);
  const rows = [rowFromValues(firstValues, columnIndexes)];

  for (let i = 2; i < lines.length; i++) {
    rows.push(rowFromValues(parseCSVLine(lines[i]), columnIndexes));
  }

  return { rawHeaders, rows, headerMapping, columnIndexes };
};

/**
 * Normalize any reasonable LinkedIn input to canonical `https://linkedin.com/in/<slug>`.
 * Accepts: bare username, missing protocol, www / national subdomains, trailing slash,
 * query / hash, mixed case. Returns '' if input can't be interpreted as a profile URL.
 */
const LINKEDIN_QUICK_REGEX = /linkedin\.com\/in\/([^\/?#\s]+)/i;

export const normalizeLinkedInUrl = (input) => {
  if (!input) return '';
  let raw = String(input).trim().replace(/^\uFEFF/, '').replace(/^@+/, '');
  if (!raw) return '';

  const quickMatch = raw.match(LINKEDIN_QUICK_REGEX);
  if (quickMatch) {
    const slug = quickMatch[1].trim();
    if (slug && LINKEDIN_SLUG_REGEX.test(slug)) {
      return `https://linkedin.com/in/${slug}`;
    }
  }

  // Bare username: no slash, no dot (e.g. "johndoe")
  if (!raw.includes('/') && !raw.includes('.')) {
    return LINKEDIN_SLUG_REGEX.test(raw) ? `https://linkedin.com/in/${raw}` : '';
  }

  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;

  let url;
  try {
    url = new URL(withProtocol);
  } catch {
    return '';
  }

  if (!LINKEDIN_HOST_REGEX.test(url.hostname)) return '';

  const match = url.pathname.match(LINKEDIN_PATH_REGEX);
  if (!match) return '';

  const slug = match[1].trim();
  if (!slug || !LINKEDIN_SLUG_REGEX.test(slug)) return '';

  return `https://linkedin.com/in/${slug}`;
};

/**
 * Normalize a phone number to E.164 (e.g. `+31555796559`).
 *
 * Tolerates the typical recruiter mess: spaces, dashes, dots, parentheses,
 * the `00` international prefix instead of `+`, and a missing `+` in front
 * of a long digits-only string. Returns '' for anything that can't be
 * confidently parsed as a valid phone number.
 */
export const normalizePhone = (input) => {
  if (!input) return '';
  let raw = String(input).trim().replace(/^\uFEFF/, '');
  if (!raw) return '';

  // Drop common formatting characters; keep a leading + if present.
  raw = raw.replace(/[\s\-().]/g, '');

  // 00 international prefix → +
  if (/^00\d/.test(raw)) {
    raw = '+' + raw.slice(2);
  }

  // Pass 1: parse with the default country. Handles both national format
  // ("0612345678") and explicit international ("+31...") in one call.
  const pass1 = parsePhoneNumberFromString(raw, DEFAULT_PHONE_COUNTRY);
  if (pass1 && pass1.isValid()) return pass1.number;

  // Pass 2: pure digits with no "+" might be E.164 with a missing "+"
  // (e.g. "31612345678" or "447911123456"). Retry with a leading "+".
  if (!raw.startsWith('+') && /^\d{10,15}$/.test(raw)) {
    const pass2 = parsePhoneNumberFromString('+' + raw);
    if (pass2 && pass2.isValid()) return pass2.number;
  }

  return '';
};

/**
 * Normalize an email: strip BOM, `mailto:`, angle brackets, trim, lowercase.
 * Returns '' if the input does not look like an email after normalization.
 */
export const normalizeEmail = (input) => {
  if (!input) return '';
  let raw = String(input).trim().replace(/^\uFEFF/, '');
  if (!raw) return '';

  raw = raw.replace(/^mailto:/i, '').trim();
  // Strip surrounding < > (e.g. "<john@example.com>")
  if (raw.startsWith('<') && raw.endsWith('>')) {
    raw = raw.slice(1, -1).trim();
  }

  const normalized = raw.toLowerCase();
  return EMAIL_REGEX.test(normalized) ? normalized : '';
};

/**
 * Parse and validate CSV.
 *
 * Returns:
 * - `rows`: full per-row breakdown for previewing, each entry has
 *   `{ rowNum, status: 'valid' | 'invalid', data, originalLinkedinUrl, errors: [{ field, message }] }`
 * - `valid`: subset of `rows` mapped to candidate payloads ready to insert
 * - `invalid`: subset of `rows` that failed validation
 * - `errors`: flat list `{ row, field, message }` for the summary view
 */
export const parseAndValidateCSV = (text) => {
  const { rawHeaders, rows: rawRows, headerMapping } = parseCSV(text);
  const detectedHeaders = rawHeaders.filter((h) => h && h.trim());

  const headerErrors = REQUIRED_FIELDS.filter((f) => !headerMapping[f]).map((f) => ({
    field: getFieldLabel(f),
    canonical: f,
    message: i18n.t('import.parser.noColumnMatched', {
      field: getFieldLabel(f),
      examples: COLUMN_SYNONYMS[f].slice(0, 6).join(', '),
    }),
  }));

  const rows = [];
  const valid = [];
  const invalid = [];
  const errors = [];
  const pendingRows = [];

  rawRows.forEach((row, index) => {
    const rowNum = index + 2; // 1-based + header row
    const mapped = { ...row };
    const originalLinkedinUrl = mapped.linkedin_url || '';
    const originalEmail = mapped.email || '';
    const originalPhone = mapped.phone || '';
    const rowErrors = [];

    const firstName = mapped.first_name?.trim();
    const linkedinUrl = mapped.linkedin_url?.trim();
    const email = mapped.email?.trim();
    const phone = mapped.phone?.trim();

    if (!firstName) {
      rowErrors.push({
        field: getFieldLabel('first_name'),
        message: i18n.t('import.parser.missingRequired'),
      });
    }

    if (!linkedinUrl) {
      rowErrors.push({
        field: getFieldLabel('linkedin_url'),
        message: i18n.t('import.parser.missingRequired'),
      });
    } else {
      const normalizedUrl = normalizeLinkedInUrl(linkedinUrl);
      if (!normalizedUrl) {
        rowErrors.push({
          field: getFieldLabel('linkedin_url'),
          message: i18n.t('import.parser.linkedinUnrecognized', { value: linkedinUrl }),
        });
      } else {
        mapped.linkedin_url = normalizedUrl;
      }
    }

    // Email is required and must be a valid address.
    if (!email) {
      rowErrors.push({
        field: getFieldLabel('email'),
        message: i18n.t('import.parser.missingRequired'),
      });
    } else {
      const normalizedEmail = normalizeEmail(email);
      if (!normalizedEmail) {
        rowErrors.push({
          field: getFieldLabel('email'),
          message: i18n.t('import.parser.emailInvalid', { value: email }),
        });
      } else {
        mapped.email = normalizedEmail;
      }
    }

    // Phone is optional, but if provided it must parse to a valid E.164 number.
    if (phone) {
      const normalizedPhone = normalizePhone(phone);
      if (!normalizedPhone) {
        rowErrors.push({
          field: getFieldLabel('phone'),
          message: i18n.t('import.parser.phoneInvalid', { value: phone }),
        });
      } else {
        mapped.phone = normalizedPhone;
      }
    }

    pendingRows.push({
      rowNum,
      mapped,
      originalLinkedinUrl,
      originalEmail,
      originalPhone,
      rowErrors,
    });
  });

  // If a LinkedIn URL appears more than once, skip every row with that URL.
  const linkedinUrlRows = new Map();
  for (const pending of pendingRows) {
    if (pending.rowErrors.length === 0 && pending.mapped.linkedin_url) {
      const url = pending.mapped.linkedin_url;
      if (!linkedinUrlRows.has(url)) linkedinUrlRows.set(url, []);
      linkedinUrlRows.get(url).push(pending.rowNum);
    }
  }

  for (const pending of pendingRows) {
    if (pending.rowErrors.length === 0 && pending.mapped.linkedin_url) {
      const rowNums = linkedinUrlRows.get(pending.mapped.linkedin_url) || [];
      if (rowNums.length > 1) {
        const otherRows = rowNums.filter((n) => n !== pending.rowNum);
        pending.rowErrors.push({
          field: getFieldLabel('linkedin_url'),
          code: 'duplicate_in_file',
          message: i18n.t('import.parser.duplicateInFile', {
            count: otherRows.length,
            rows: otherRows.join(', '),
          }),
        });
      }
    }

    const { rowNum, mapped, originalLinkedinUrl, originalEmail, originalPhone, rowErrors } = pending;
    const status = rowErrors.length > 0 ? 'invalid' : 'valid';
    const rowResult = {
      rowNum,
      status,
      data: mapped,
      originalLinkedinUrl,
      originalEmail,
      originalPhone,
      errors: rowErrors,
    };
    rows.push(rowResult);

    if (status === 'invalid') {
      rowErrors.forEach((e) => errors.push({ row: rowNum, ...e }));
      invalid.push({ row: rowNum, data: mapped });
    } else {
      valid.push(mapped);
    }
  }

  return { rows, valid, invalid, errors, headerMapping, headerErrors, detectedHeaders };
};

/**
 * Validate a single manually entered candidate (same rules as CSV import rows).
 */
export const validateManualCandidateForm = (values) => {
  const fieldErrors = {};
  const mapped = {
    first_name: String(values?.first_name ?? '').trim(),
    last_name: String(values?.last_name ?? '').trim(),
    linkedin_url: String(values?.linkedin_url ?? '').trim(),
    email: String(values?.email ?? '').trim(),
    current_company: String(values?.current_company ?? '').trim(),
    current_position: String(values?.current_position ?? '').trim(),
    location: String(values?.location ?? '').trim(),
    phone: String(values?.phone ?? '').trim(),
  };

  if (!mapped.first_name) {
    fieldErrors.first_name = i18n.t('import.parser.firstNameRequired');
  }

  if (!mapped.linkedin_url) {
    fieldErrors.linkedin_url = i18n.t('import.parser.linkedinRequired');
  } else {
    const normalizedUrl = normalizeLinkedInUrl(mapped.linkedin_url);
    if (!normalizedUrl) {
      fieldErrors.linkedin_url = i18n.t('import.parser.linkedinUnrecognized', {
        value: mapped.linkedin_url,
      });
    } else {
      mapped.linkedin_url = normalizedUrl;
    }
  }

  if (!mapped.email) {
    fieldErrors.email = i18n.t('import.parser.emailRequired');
  } else {
    const normalizedEmail = normalizeEmail(mapped.email);
    if (!normalizedEmail) {
      fieldErrors.email = i18n.t('import.parser.emailInvalidShort');
    } else {
      mapped.email = normalizedEmail;
    }
  }

  if (mapped.phone) {
    const normalizedPhone = normalizePhone(mapped.phone);
    if (!normalizedPhone) {
      fieldErrors.phone = i18n.t('import.parser.phoneInvalidShort');
    } else {
      mapped.phone = normalizedPhone;
    }
  }

  ['last_name', 'current_company', 'current_position', 'location', 'phone'].forEach((field) => {
    if (!mapped[field]) delete mapped[field];
  });

  return {
    isValid: Object.keys(fieldErrors).length === 0,
    fieldErrors,
    data: mapped,
  };
};

/**
 * Convert a parsed candidate into a DB payload.
 * first_name / last_name become full_name. Location is a CSV/form field;
 * candidates stores it in city (there is no location column).
 */
export const toCandidateDbPayload = (candidate) => {
  const { first_name, last_name, location, ...rest } = candidate || {};
  const fullName = `${(first_name || '').trim()} ${(last_name || '').trim()}`.trim();
  const payload = fullName ? { ...rest, full_name: fullName } : { ...rest };
  const place = String(location ?? '').trim();
  if (place && !String(payload.city ?? '').trim()) {
    payload.city = place;
  }
  return payload;
};

const EXCEL_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Whether the file is a supported candidate import (CSV or Excel).
 */
export const isImportFile = (file) => {
  if (!file?.name) return false;
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) return true;
  if (EXCEL_EXTENSIONS.some((ext) => name.endsWith(ext))) return true;
  const type = file.type || '';
  return (
    type === 'text/csv'
    || type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    || type === 'application/vnd.ms-excel'
  );
};

/**
 * Read file as text
 */
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, 'UTF-8');
  });
};

/**
 * Read CSV or Excel (.xlsx / .xls) and return CSV text for parseAndValidateCSV.
 */
export const readImportFile = async (file) => {
  if (!isImportFile(file)) {
    throw new Error(i18n.t('import.parser.invalidFileType'));
  }

  const name = file.name.toLowerCase();
  if (EXCEL_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    const XLSX = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheetName = workbook.SheetNames?.[0];
    if (!sheetName) {
      throw new Error(i18n.t('import.parser.excelNoSheets'));
    }
    const sheet = workbook.Sheets[sheetName];
    const csv = XLSX.utils.sheet_to_csv(sheet);
    if (!String(csv || '').trim()) {
      throw new Error(i18n.t('import.parser.excelEmpty'));
    }
    return csv;
  }

  return readFileAsText(file);
};
